import React from 'react';
import { Mail, AlertTriangle, ArrowRight, X } from 'lucide-react';

interface SubscriptionErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorMessage?: string;
}

const SubscriptionErrorModal: React.FC<SubscriptionErrorModalProps> = ({
  isOpen,
  onClose,
  errorMessage,
}) => {
  if (!isOpen) return null;

  const isSubscriptionError = errorMessage?.includes('greg.henkhaus@gmail.com') || 
                              errorMessage?.includes('subscription') ||
                              errorMessage?.includes('capacity');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 space-y-6 transform transition-all scale-100">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {isSubscriptionError ? (
          <>
            {/* Friendly Icon with Background */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-200 to-amber-300 dark:from-amber-800 dark:to-amber-900 rounded-full blur-xl opacity-50 animate-pulse"></div>
                <div className="relative bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 rounded-full p-6 border-2 border-amber-200 dark:border-amber-700">
                  <AlertTriangle className="h-16 w-16 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
                </div>
              </div>
            </div>
            
            {/* Content Card with Gradient Border */}
            <div className="relative bg-gradient-to-br from-amber-50/50 via-white to-green-50/50 dark:from-amber-900/10 dark:via-gray-800 dark:to-green-900/10 rounded-xl p-6 border-2 border-amber-200/50 dark:border-amber-700/50 shadow-lg">
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Almost There!
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  To accept this invitation, you'll need an active subscription with available capacity.
                </p>
                <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 border border-amber-200/30 dark:border-amber-700/30">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    We'd love to help you get set up!
                  </p>
                  <a 
                    href="mailto:greg.henkhaus@gmail.com?subject=Subscription Request&body=Hi Greg,%0D%0A%0D%0AI'd like to activate my subscription or add capacity to my current subscription. Could you please help me with this?%0D%0A%0D%0AThank you!"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                  >
                    <Mail className="h-5 w-5" />
                    Contact Greg Henkhaus
                    <ArrowRight className="h-4 w-4" />
                  </a>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
                    Click to send an email and we'll get you set up right away!
                  </p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-red-200 dark:bg-red-900 rounded-full blur-xl opacity-30"></div>
                <div className="relative bg-red-50 dark:bg-red-900/30 rounded-full p-6 border-2 border-red-200 dark:border-red-700">
                  <svg className="h-12 w-12 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-red-700 dark:text-red-300 font-medium">
                {errorMessage || 'An error occurred.'}
              </p>
            </div>
          </>
        )}

        {/* Close Button */}
        <div className="pt-4">
          <button
            onClick={onClose}
            className="w-full py-2 px-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionErrorModal;

